import { useEffect, useMemo, useState, type FormEvent } from "react";
import { adminPhotoUrl, api, type AccessCode, type ActivitySummary, type Photo, type Stats, type Submission, type TargetSummary } from "./api";

const maxPhotoBytes = 15 * 1024 * 1024;
const maxPhotosPerSubmission = 20;
const supportedPhotoTypes = new Set(["image/jpeg", "image/png", "image/webp", "image/heic"]);
const targetImportSample = `target_code,target_name,address
A001,第一走访对象,示例小区 1 号楼
A002,第二走访对象,示例小区 2 号楼`;

function validateSelectedFiles(files: File[]): string | null {
  if (files.length === 0) return "请选择照片";
  if (files.length > maxPhotosPerSubmission) return "单次最多上传 20 张照片";
  const invalidType = files.find((file) => !supportedPhotoTypes.has(file.type.toLowerCase()));
  if (invalidType) return `文件 ${invalidType.name} 格式不支持，仅支持 JPG、PNG、WEBP、HEIC`;
  const oversized = files.find((file) => file.size > maxPhotoBytes);
  if (oversized) return `文件 ${oversized.name} 超过 15MB，请压缩后再上传`;
  return null;
}

function Icon({ name, size = 18 }: { name: "check" | "copy" | "download" | "file" | "key" | "lock" | "logout" | "refresh" | "search" | "shield" | "trash" | "upload"; size?: number }) {
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const paths = {
    check: <><path d="M20 6 9 17l-5-5" /><circle cx="12" cy="12" r="10" /></>,
    copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h5" /></>,
    key: <><circle cx="7.5" cy="15.5" r="4.5" /><path d="M11 12 21 2" /><path d="m16 7 2 2" /><path d="m19 4 2 2" /></>,
    lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></>,
    refresh: <><path d="M21 12a9 9 0 0 1-15.5 6.2" /><path d="M3 12A9 9 0 0 1 18.5 5.8" /><path d="M18 2v4h4" /><path d="M6 22v-4H2" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" /></>,
    trash: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v5" /><path d="M14 11v5" /></>,
    upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8 12 3 7 8" /><path d="M12 3v12" /></>
  } as const;
  return <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" {...common}>{paths[name]}</svg>;
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function StatusPill({ status }: { status: string }) {
  const label = status === "uploaded" ? "已上传" : status === "active" ? "启用" : status === "disabled" ? "禁用" : status === "revoked" ? "已作废" : "未上传";
  return <span className={`pill pill-${status}`}>{label}</span>;
}

function Message({ text, tone = "info" }: { text: string; tone?: "info" | "error" | "success" }) {
  if (!text) return null;
  return <div className={`message message-${tone}`}>{text}</div>;
}

function UploadApp() {
  const isActivityPage = window.location.pathname.startsWith("/a/");
  const [code, setCode] = useState("");
  const [activityCode, setActivityCode] = useState("");
  const [activity, setActivity] = useState<ActivitySummary | null>(null);
  const [targets, setTargets] = useState<TargetSummary[]>([]);
  const [targetId, setTargetId] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [remark, setRemark] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [success, setSuccess] = useState<{ photoCount: number; targetPhotoCount: number; targetName: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const pathMatch = window.location.pathname.match(/^\/a\/([^/]+)/);
      const urlCode = new URL(window.location.href).searchParams.get("code")?.trim() ?? "";
      try {
        const result = pathMatch ? await api.activityById(decodeURIComponent(pathMatch[1])) : await api.defaultActivity();
        if (cancelled) return;
        setActivity(result.activity);
        setActivityCode(result.activity.code ?? "");
        if (urlCode) {
          setCode(urlCode);
          await verifyForActivity(result.activity, urlCode);
          return;
        }
        try {
          const session = await api.uploadSession();
          if (cancelled) return;
          setActivity(session.activity);
          setActivityCode(session.activity.code ?? "");
          setTargets(session.targets);
          setTargetId("");
          setAuthenticated(true);
          setSuccess(null);
          if (!pathMatch) window.history.replaceState({}, "", `/a/${encodeURIComponent(session.activity.id)}`);
        } catch {
          // No saved upload session; keep the access-code form visible.
        }
      } catch (error) {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "活动加载失败");
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function verifyForActivity(nextActivity: ActivitySummary, nextCode: string) {
    setBusy(true);
    setMessage("");
    try {
      await api.verifyCode(nextActivity.id, nextCode);
      const result = await api.uploadTargets();
      setActivity(result.activity);
      setTargets(result.targets);
      setTargetId("");
      setAuthenticated(true);
      setSuccess(null);
      window.history.replaceState({}, "", `/a/${encodeURIComponent(result.activity.id)}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "访问码验证失败");
    } finally {
      setBusy(false);
    }
  }

  async function switchActivity(event: FormEvent) {
    event.preventDefault();
    const nextCode = activityCode.trim();
    if (!nextCode) {
      setMessage("请输入活动编码");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const result = await api.resolveActivity(nextCode);
      setActivity(result.activity);
      setAuthenticated(false);
      setTargets([]);
      setTargetId("");
      setCode("");
      setSuccess(null);
      window.history.replaceState({}, "", `/a/${encodeURIComponent(result.activity.id)}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "活动不存在");
    } finally {
      setBusy(false);
    }
  }

  async function verify(event?: FormEvent) {
    event?.preventDefault();
    if (!activity) {
      setMessage("请先选择活动");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await verifyForActivity(activity, code);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "访问码验证失败");
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!targetId) {
      setMessage("请通过下拉列表选择一个走访对象");
      return;
    }
    if (!visitorName.trim() || files.length === 0) {
      setMessage("请填写走访志愿者并选择照片");
      return;
    }
    const fileError = validateSelectedFiles(files);
    if (fileError) {
      setMessage(fileError);
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const submission = await api.createSubmission({ targetId, visitorName, visitStatus: "已完成", remark, photoCount: files.length });
      for (const file of files) await api.uploadPhoto(submission.id, file);
      const completed = await api.completeSubmission(submission.id);
      setSuccess({
        photoCount: completed.photoCount,
        targetPhotoCount: completed.targetPhotoCount,
        targetName: `${submission.target.target_code}-${submission.target.target_name}`
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "上传失败");
    } finally {
      setBusy(false);
    }
  }

  function resetUploadForm() {
    setTargetId("");
    setVisitorName("");
    setRemark("");
    setFiles([]);
    setSuccess(null);
    setMessage("");
  }

  return (
    <main className="upload-shell">
      <section className="upload-panel">
        <div className="brand-row">
          <div className="brand-mark"><Icon name="upload" size={22} /></div>
          <div>
            <h1>{activity ? `${activity.name}照片上传` : "照片上传"}</h1>
          </div>
        </div>

        {!isActivityPage && !authenticated && (
        <form className="activity-switcher" onSubmit={switchActivity}>
          <div>
            <span>当前活动</span>
            <strong>{activity?.name ?? "加载中..."}</strong>
          </div>
          <input value={activityCode} onChange={(event) => setActivityCode(event.target.value)} placeholder="输入活动编码" />
          <button type="submit" disabled={busy}>切换活动</button>
        </form>
        )}

        {checkingSession && <Message text="正在检查当前浏览器的访问状态..." />}

        {!checkingSession && !authenticated && (
          <form className="form-stack" onSubmit={verify}>
            <label>本次活动访问码</label>
            <input autoFocus value={code} onChange={(event) => setCode(event.target.value)} placeholder="请输入访问码" />
            <button type="submit" className="primary" disabled={busy}>{busy ? "验证中..." : "进入上传"}</button>
            <Message text={message} tone="error" />
          </form>
        )}

        {authenticated && !success && (
          <div className="form-stack">
            <label htmlFor="photo-step-target">走访对象</label>
            <select id="photo-step-target" value={targetId} onChange={(event) => setTargetId(event.target.value)}>
              <option value="">请选择走访对象</option>
              {targets.map((target) => <option key={target.id} value={target.id}>{target.target_code} - {target.target_name}</option>)}
            </select>
            <label htmlFor="visit-photos">走访照片</label>
            <input id="visit-photos" type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple onChange={(event) => {
              const nextFiles = Array.from(event.target.files ?? []);
              const fileError = validateSelectedFiles(nextFiles);
              if (fileError && nextFiles.length > 0) {
                setFiles([]);
                setMessage(fileError);
                event.currentTarget.value = "";
                return;
              }
              setMessage("");
              setFiles(nextFiles);
            }} />
            <div className="file-list">{files.length ? `${files.length} 张照片待上传` : "单次最多 20 张，单张图片建议不要超过 5MB"}</div>
            <label htmlFor="visitor-name">走访志愿者</label>
            <input id="visitor-name" value={visitorName} onChange={(event) => setVisitorName(event.target.value)} placeholder="例如：张三" />
            <label htmlFor="visit-remark">备注</label>
            <textarea id="visit-remark" value={remark} onChange={(event) => setRemark(event.target.value)} placeholder="选填，记录补拍说明或现场情况" />
            <div className="split-actions">
              <button className="secondary" onClick={() => setAuthenticated(false)}>返回</button>
              <button className="primary" disabled={busy} onClick={submit}>{busy ? "上传中..." : "提交上传"}</button>
            </div>
            <Message text={message} tone="error" />
          </div>
        )}

        {authenticated && success && (
          <div className="success-box">
            <Icon name="check" size={42} />
            <h2>上传成功</h2>
            <p>目标对象：{success.targetName}</p>
            <p>本次上传照片：{success.photoCount} 张</p>
            <p>累计照片：{success.targetPhotoCount} 张</p>
            <button className="primary" onClick={resetUploadForm}>返回继续上传其它照片</button>
          </div>
        )}
      </section>
    </main>
  );
}

function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState("");
  const [setupRequired, setSetupRequired] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.adminSetupStatus()
      .then((result) => setSetupRequired(result.setupRequired))
      .catch(() => setSetupRequired(false))
      .finally(() => setCheckingSetup(false));
  }, []);

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (setupRequired) {
        await api.setupAdminPassword(password);
      } else {
        await api.adminLogin(password);
      }
      onLogin();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : setupRequired ? "设置失败" : "登录失败");
    } finally {
      setBusy(false);
    }
  }

  if (checkingSetup) {
    return (
      <main className="admin-login">
        <section className="login-panel">
          <div className="brand-mark"><Icon name="lock" size={24} /></div>
          <h1>照片收集后台</h1>
          <p>正在检查后台初始化状态。</p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-login">
      <form className="login-panel" onSubmit={submit}>
        <div className="brand-mark"><Icon name="lock" size={24} /></div>
        <h1>{setupRequired ? "设置后台初始密码" : "照片收集后台"}</h1>
        <p>{setupRequired ? "首次部署需要先设置管理员密码，保存后会直接进入后台。" : "管理员登录后可查看进度、导入目标、管理访问码。"}</p>
        <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={setupRequired ? "设置管理员密码" : "管理员密码"} />
        <button type="submit" className="primary" disabled={busy}>{busy ? setupRequired ? "保存中..." : "登录中..." : setupRequired ? "保存并进入后台" : "登录后台"}</button>
        <Message text={message} tone="error" />
      </form>
    </main>
  );
}

function StatGrid({ stats }: { stats: Stats | null }) {
  const items = [
    ["目标总数", stats?.totalTargets ?? 0],
    ["已上传", stats?.uploadedTargets ?? 0],
    ["未上传", stats?.pendingTargets ?? 0],
    ["照片总数", stats?.photoTotal ?? 0],
    ["今日新增", stats?.todayPhotos ?? 0]
  ];
  return <div className="stat-grid">{items.map(([label, value]) => <div className="stat" key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>;
}

function ActivityManager({ activities, selectedActivityId, onSelect, refresh }: {
  activities: ActivitySummary[];
  selectedActivityId: string;
  onSelect: (id: string) => void;
  refresh: () => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [drafts, setDrafts] = useState<Record<string, { name: string; code: string; status: string }>>({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    setDrafts(Object.fromEntries(activities.map((activity) => [
      activity.id,
      { name: activity.name, code: activity.code, status: activity.status === "disabled" ? "disabled" : "active" }
    ])));
  }, [activities]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    try {
      const result = await api.createActivity({ name, code });
      setName("");
      setCode("");
      onSelect(result.activity.id);
      refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "创建活动失败");
    }
  }

  async function save(activity: ActivitySummary) {
    setMessage("");
    const draft = drafts[activity.id];
    if (!draft) return;
    try {
      await api.updateActivity(activity.id, {
        name: draft.name,
        code: draft.code,
        status: draft.status === "disabled" ? "disabled" : "active",
        isDefault: activity.is_default === 1
      });
      refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "更新活动失败");
    }
  }

  async function setDefault(activityId: string) {
    setMessage("");
    try {
      await api.updateActivity(activityId, { isDefault: true });
      refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "设置默认活动失败");
    }
  }

  async function remove(activity: ActivitySummary) {
    setMessage("");
    if (activity.is_default === 1) {
      setMessage("默认活动不能删除");
      return;
    }
    if (!window.confirm(`删除活动“${activity.name}”会同时删除相关目标对象、提交记录和照片，确定删除？`)) return;
    try {
      await api.deleteActivity(activity.id);
      refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "删除活动失败");
    }
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <h2><Icon name="file" size={18} /> 活动管理</h2>
      </div>
      <form className="activity-form" onSubmit={create}>
        <input value={name} onChange={(event) => setName(event.target.value)} placeholder="新活动名称" />
        <input value={code} onChange={(event) => setCode(event.target.value)} placeholder="活动编码" />
        <button className="primary" type="submit">新建活动</button>
      </form>
      <Message text={message} tone="error" />
      <div className="activity-list">
        {activities.map((activity) => (
          <div className={activity.id === selectedActivityId ? "activity-row selected" : "activity-row"} key={activity.id}>
            <div className="activity-edit">
              <input value={drafts[activity.id]?.name ?? activity.name} onChange={(event) => setDrafts((current) => ({
                ...current,
                [activity.id]: { ...current[activity.id], name: event.target.value }
              }))} />
              <input value={drafts[activity.id]?.code ?? activity.code} onChange={(event) => setDrafts((current) => ({
                ...current,
                [activity.id]: { ...current[activity.id], code: event.target.value }
              }))} />
              <span>{activity.id}</span>
            </div>
            <select value={drafts[activity.id]?.status ?? "active"} onChange={(event) => setDrafts((current) => ({
              ...current,
              [activity.id]: { ...current[activity.id], status: event.target.value }
            }))}>
              <option value="active">启用</option>
              <option value="disabled">停用</option>
            </select>
            {activity.is_default === 1 && <span className="default-badge">默认</span>}
            <div className="row-actions">
              <button onClick={() => onSelect(activity.id)}>选择</button>
              <button onClick={() => setDefault(activity.id)} disabled={activity.is_default === 1}>设为默认</button>
              <button onClick={() => save(activity)}>保存</button>
              <button className="danger" onClick={() => remove(activity)} disabled={activity.is_default === 1}>删除</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CodeManager({ codes, activities, selectedActivityId, onSelectActivity, refresh }: { codes: AccessCode[]; activities: ActivitySummary[]; selectedActivityId: string; onSelectActivity: (id: string) => void; refresh: () => void }) {
  const [label, setLabel] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [createdCode, setCreatedCode] = useState("");
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"error" | "success">("error");
  const [visibleCodes, setVisibleCodes] = useState<Set<string>>(new Set());

  async function create() {
    setMessage("");
    setMessageTone("error");
    if (!selectedActivityId) {
      setMessage("请先选择活动");
      return;
    }
    try {
      const result = await api.createCode({
        activityId: selectedActivityId,
        label: label || "访问码",
        code: manualCode || undefined,
        expiresAt: expiresAt || undefined,
        maxUses: maxUses ? Number(maxUses) : null
      });
      setCreatedCode(result.code);
      setLabel("");
      setManualCode("");
      setExpiresAt("");
      setMaxUses("");
      refresh();
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "创建访问码失败");
    }
  }

  async function update(id: string, action: "disable" | "enable" | "revoke") {
    await api.setCodeStatus(id, action);
    refresh();
  }

  async function copyAccessLink(code: AccessCode) {
    const url = new URL(`/a/${encodeURIComponent(code.activity_id)}`, window.location.origin);
    url.searchParams.set("code", code.plain_code);
    await navigator.clipboard.writeText(url.toString());
    setMessageTone("success");
    setMessage("带访问码的链接已复制");
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <h2><Icon name="key" size={18} /> 访问码管理</h2>
        <select value={selectedActivityId} onChange={(event) => onSelectActivity(event.target.value)}>
          {activities.map((activity) => <option key={activity.id} value={activity.id}>{activity.name}</option>)}
        </select>
      </div>
      <div className="code-form">
        <input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="访问码名称" />
        <input value={manualCode} onChange={(event) => setManualCode(event.target.value)} placeholder="访问码（留空自动生成）" />
        <label className="inline-field">过期时间<input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} /></label>
        <input type="number" value={maxUses} onChange={(event) => setMaxUses(event.target.value)} placeholder="最大使用次数" />
        <button className="primary" onClick={create}>新建访问码</button>
      </div>
      <Message text={message} tone={messageTone} />
      {createdCode && <Message text={`新访问码：${createdCode}`} tone="success" />}
      <div className="code-list">
        {codes.map((code) => (
          <div className="code-row code-card" key={code.id}>
            <div className="code-card-main">
              <div className="code-card-title">
                <strong>{code.label || "访问码"}</strong>
                <StatusPill status={code.status} />
              </div>
              <span className="code-secret">访问码：{visibleCodes.has(code.id) ? code.plain_code : "••••••"}</span>
              <div className="code-card-meta">
                <span>已用 {code.used_count}{code.max_uses ? ` / ${code.max_uses}` : ""}</span>
                <span>创建时间 {formatDate(code.created_at)}</span>
                {code.expires_at && <span>过期时间 {formatDate(code.expires_at)}</span>}
              </div>
            </div>
            <div className="row-actions code-card-actions">
              <button onClick={() => setVisibleCodes((current) => {
                const next = new Set(current);
                if (next.has(code.id)) next.delete(code.id);
                else next.add(code.id);
                return next;
              })}>{visibleCodes.has(code.id) ? "隐藏" : "显示"}</button>
              {visibleCodes.has(code.id) && <button onClick={() => copyAccessLink(code)}><Icon name="copy" size={15} /> 复制链接</button>}
              {code.status !== "active" && <button onClick={() => update(code.id, "enable")}>启用</button>}
              {code.status === "active" && <button onClick={() => update(code.id, "disable")}>禁用</button>}
              <button className="danger" onClick={() => update(code.id, "revoke")}>作废</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PasswordManager() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [tone, setTone] = useState<"error" | "success">("success");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await api.changeAdminPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setTone("success");
      setMessage("后台密码已更新");
    } catch (error) {
      setTone("error");
      setMessage(error instanceof Error ? error.message : "修改密码失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <h2><Icon name="lock" size={18} /> 后台密码</h2>
      </div>
      <form className="password-form" onSubmit={submit}>
        <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} placeholder="当前密码" />
        <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="新密码" />
        <button className="primary" type="submit" disabled={busy}>{busy ? "保存中..." : "修改密码"}</button>
      </form>
      <Message text={message} tone={tone} />
    </section>
  );
}

function TargetDetail({ targetId, onChanged }: { targetId: string | null; onChanged: () => void }) {
  const [detail, setDetail] = useState<{ activity: ActivitySummary; target: TargetSummary; submissions: Submission[]; photos: Photo[] } | null>(null);
  const [preview, setPreview] = useState<Photo | null>(null);
  const [message, setMessage] = useState("");
  const [deletingPhotoId, setDeletingPhotoId] = useState("");

  async function loadDetail(id: string) {
    setMessage("");
    try {
      const nextDetail = await api.targetDetail(id);
      setDetail(nextDetail);
      setPreview((current) => current && nextDetail.photos.some((photo) => photo.id === current.id) ? current : null);
    } catch {
      setDetail(null);
    }
  }

  useEffect(() => {
    setPreview(null);
    setMessage("");
    if (!targetId) return;
    void loadDetail(targetId);
  }, [targetId]);

  async function removePhoto(photo: Photo) {
    setMessage("");
    setDeletingPhotoId(photo.id);
    try {
      await api.deletePhoto(photo.id);
      if (targetId) await loadDetail(targetId);
      onChanged();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "删除照片失败");
    } finally {
      setDeletingPhotoId("");
    }
  }

  if (!targetId) return <aside className="detail-panel"><p>选择一个目标查看提交记录和照片。</p></aside>;
  if (!detail) return <aside className="detail-panel"><p>加载中...</p></aside>;
  return (
    <aside className="detail-panel">
      <h2>{detail.target.target_code} {detail.target.target_name}</h2>
      <p>走访活动：{detail.activity.name}</p>
      <Message text={message} tone="error" />
      <div className="thumb-grid">
        {detail.photos.map((photo) => (
          <div className="thumb-item" key={photo.id}>
            <button className="thumb-button" onClick={() => setPreview(photo)}>
              <img src={adminPhotoUrl(photo, "thumb")} alt={photo.original_filename || photo.r2_key} loading="lazy" />
            </button>
            <button className="photo-delete" aria-label={`删除照片 ${photo.original_filename || photo.r2_key}`} disabled={deletingPhotoId === photo.id} onClick={() => removePhoto(photo)}>
              <Icon name="trash" size={13} />
            </button>
          </div>
        ))}
        {detail.photos.length === 0 && <div className="empty">暂无照片</div>}
      </div>
      {preview && (
        <div className="photo-preview" role="dialog" aria-label="照片预览" onClick={() => setPreview(null)}>
          <div className="photo-preview-inner" onClick={(event) => event.stopPropagation()}>
            <div className="preview-actions">
              <button className="preview-close" onClick={() => setPreview(null)}>关闭</button>
              <button className="preview-delete" disabled={deletingPhotoId === preview.id} onClick={() => removePhoto(preview)}><Icon name="trash" size={14} /> 删除</button>
            </div>
            <img src={adminPhotoUrl(preview, "preview")} alt={preview.original_filename || preview.r2_key} />
          </div>
        </div>
      )}
      <h3>提交记录</h3>
      <div className="submission-list">
        {detail.submissions.map((submission) => (
          <div key={submission.id}>
            <strong>{submission.visitor_name}</strong>
            <span>{formatDate(submission.completed_at || submission.created_at)} · {submission.visit_status} · {submission.photo_count} 张</span>
            {submission.remark && <p>{submission.remark}</p>}
          </div>
        ))}
      </div>
      <h3>R2 Key</h3>
      <div className="key-list">{detail.photos.map((photo) => <code key={photo.id}>{photo.r2_key}</code>)}</div>
    </aside>
  );
}

function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [activeMenu, setActiveMenu] = useState<"targets" | "activities" | "codes" | "password">("targets");
  const [stats, setStats] = useState<Stats | null>(null);
  const [activities, setActivities] = useState<ActivitySummary[]>([]);
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [targets, setTargets] = useState<TargetSummary[]>([]);
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [csv, setCsv] = useState("");
  const [message, setMessage] = useState("");

  const selectedTarget = useMemo(() => targets.find((target) => target.id === selectedTargetId), [targets, selectedTargetId]);

  async function refresh(nextActivityId = selectedActivityId) {
    const nextActivitiesResult = await api.activities();
    const nextActivities = nextActivitiesResult.activities;
    const resolvedActivityId = nextActivityId || nextActivities.find((activity) => activity.is_default === 1)?.id || nextActivities[0]?.id || "";
    const [nextStats, nextTargets, nextCodes] = await Promise.all([
      api.stats(resolvedActivityId),
      api.targets(query, status, "", resolvedActivityId),
      api.codes(resolvedActivityId)
    ]);
    setActivities(nextActivities);
    setSelectedActivityId(resolvedActivityId);
    setStats(nextStats);
    setTargets(nextTargets.targets);
    setCodes(nextCodes.codes);
    if (!nextTargets.targets.some((target) => target.id === selectedTargetId)) {
      setSelectedTargetId(nextTargets.targets[0]?.id ?? null);
    }
  }

  useEffect(() => {
    refresh().catch(() => undefined);
  }, []);

  async function importCsv() {
    setMessage("");
    try {
      const result = await api.importTargets(csv, selectedActivityId);
      setMessage(`已导入 ${result.imported} 个目标对象`);
      setCsv("");
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "导入失败");
    }
  }

  async function logout() {
    await api.adminLogout();
    onLogout();
  }

  async function removeTarget(targetId: string) {
    await api.deleteTarget(targetId);
    if (selectedTargetId === targetId) setSelectedTargetId(null);
    await refresh();
  }

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <h1>照片收集后台</h1>
          <p>查看目标进度、照片数量、访问码状态和导出记录。</p>
        </div>
        <div className="header-actions">
          <button onClick={() => refresh()}><Icon name="refresh" size={16} /> 刷新</button>
          <a className="button-like" href={`/api/admin/export.csv${selectedActivityId ? `?activityId=${encodeURIComponent(selectedActivityId)}` : ""}`}><Icon name="download" size={16} /> 导出 CSV</a>
          <button onClick={logout}><Icon name="logout" size={16} /> 退出</button>
        </div>
      </header>
      <StatGrid stats={stats} />
      <nav className="admin-tabs" aria-label="后台功能菜单">
        <button className={activeMenu === "targets" ? "active" : ""} onClick={() => setActiveMenu("targets")}>目标管理</button>
        <button className={activeMenu === "activities" ? "active" : ""} onClick={() => setActiveMenu("activities")}>活动管理</button>
        <button className={activeMenu === "codes" ? "active" : ""} onClick={() => setActiveMenu("codes")}>访问码管理</button>
        <button className={activeMenu === "password" ? "active" : ""} onClick={() => setActiveMenu("password")}>后台密码</button>
      </nav>
      <div className="admin-layout">
        <section className="main-column">
          {activeMenu === "targets" && (
          <section className="panel">
            <div className="panel-heading">
              <h2><Icon name="file" size={18} /> 目标对象</h2>
              <div className="filters">
                <div className="search-box"><Icon name="search" size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索编号或名称" /></div>
                <label className="filter-field">活动名称
                  <select value={selectedActivityId} onChange={(event) => setSelectedActivityId(event.target.value)}>
                    {activities.map((activity) => <option key={activity.id} value={activity.id}>{activity.name}</option>)}
                  </select>
                </label>
                <select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">全部状态</option>
                  <option value="pending">未上传</option>
                  <option value="uploaded">已上传</option>
                </select>
                <button onClick={() => refresh()}>筛选</button>
              </div>
            </div>
            <div className="import-box">
              <textarea value={csv} onChange={(event) => setCsv(event.target.value)} placeholder="粘贴 CSV：activity_name,target_code,target_name" />
              <button className="primary" onClick={importCsv}>导入目标</button>
            </div>
            <div className="csv-sample">
              <div>
                <strong>CSV 示例</strong>
                <span>必填字段：target_code、target_name；可选字段：address。导入会写入当前选择的活动。</span>
              </div>
              <pre>{targetImportSample}</pre>
              <button onClick={() => setCsv(targetImportSample)}>填入示例</button>
            </div>
            <Message text={message} tone={message.startsWith("已导入") ? "success" : "error"} />
            <table>
              <thead>
                <tr>
                  <th>活动名称</th>
                  <th>目标编号</th>
                  <th>目标名称</th>
                  <th>状态</th>
                  <th>提交次数</th>
                  <th>照片数量</th>
                  <th>最近上传</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => (
                  <tr className={selectedTarget?.id === target.id ? "selected clickable-row" : "clickable-row"} key={target.id} onClick={() => setSelectedTargetId(target.id)}>
                    <td>{target.activity_name || "默认走访活动"}</td>
                    <td>{target.target_code}</td>
                    <td>{target.target_name}</td>
                    <td><StatusPill status={target.status} /></td>
                    <td>{target.submission_count ?? 0}</td>
                    <td>{target.photo_count ?? 0}</td>
                    <td>{formatDate(target.last_uploaded_at)}</td>
                    <td><button className="danger" onClick={(event) => { event.stopPropagation(); void removeTarget(target.id); }}>删除</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          )}
          {activeMenu === "activities" && (
          <ActivityManager
            activities={activities}
            selectedActivityId={selectedActivityId}
            onSelect={(activityId) => {
              setSelectedActivityId(activityId);
              void refresh(activityId);
            }}
            refresh={() => refresh()}
          />
          )}
          {activeMenu === "codes" && (
          <CodeManager
            codes={codes}
            activities={activities}
            selectedActivityId={selectedActivityId}
            onSelectActivity={(activityId) => {
              setSelectedActivityId(activityId);
              void refresh(activityId);
            }}
            refresh={() => refresh()}
          />
          )}
          {activeMenu === "password" && (
          <PasswordManager />
          )}
        </section>
        {activeMenu === "targets" && <TargetDetail targetId={selectedTargetId} onChanged={() => { void refresh(); }} />}
      </div>
    </main>
  );
}

export default function App() {
  const [adminAuthed, setAdminAuthed] = useState(false);
  const [checkingAdminSession, setCheckingAdminSession] = useState(window.location.pathname.startsWith("/admin"));
  useEffect(() => {
    if (!window.location.pathname.startsWith("/admin")) return;
    api.adminSession()
      .then(() => setAdminAuthed(true))
      .catch(() => setAdminAuthed(false))
      .finally(() => setCheckingAdminSession(false));
  }, []);
  if (window.location.pathname.startsWith("/admin")) {
    if (checkingAdminSession) {
      return (
        <main className="admin-login">
          <section className="login-panel">
            <div className="brand-mark"><Icon name="lock" size={24} /></div>
            <h1>照片收集后台</h1>
            <p>正在检查当前浏览器的后台登录状态。</p>
          </section>
        </main>
      );
    }
    return adminAuthed ? <AdminDashboard onLogout={() => setAdminAuthed(false)} /> : <AdminLogin onLogin={() => setAdminAuthed(true)} />;
  }
  return <UploadApp />;
}
